<template>
  <transition name="modal-fade">
    <div v-if="show" class="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm">
      <div class="relative w-full max-w-6xl bg-white rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
        
        <!-- Header -->
        <div class="bg-gradient-to-r from-blue-600 to-indigo-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between text-white flex-shrink-0">
          <div class="flex items-center gap-2 sm:gap-3">
            <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 class="text-base sm:text-lg font-bold">{{ $t('pdfPreview.title') }}</h3>
          </div>
          <button @click="$emit('close')" class="hover:bg-white/20 p-2 rounded-lg text-white transition-colors">
            <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 min-h-0 p-2 sm:p-4 bg-gray-100 overflow-hidden relative">
          <div class="w-full h-full bg-white rounded-lg sm:rounded-xl relative shadow-inner overflow-hidden">
            
            <!-- Loading Indicator -->
            <div v-if="loading || fetchingPdf" class="absolute inset-0 flex items-center justify-center bg-white z-20">
              <div class="text-center p-4">
                <svg class="animate-spin h-10 w-10 sm:h-12 sm:w-12 text-blue-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p class="font-medium text-gray-600 text-sm sm:text-base">{{ fetchingPdf ? $t('pdfPreview.loadingPdf') : $t('pdfPreview.generatingPreview') }}</p>
              </div>
            </div>

            <div class="w-full h-full overflow-auto p-2 sm:p-4">
              <!-- Error Message -->
              <div v-if="error || fetchError" class="max-w-2xl mx-auto py-6 sm:py-10 text-center px-4">
                <svg class="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 class="text-lg sm:text-xl font-bold text-gray-800 mb-2">{{ $t('pdfPreview.errorTitle') }}</h4>
                <p class="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">{{ error || fetchError }}</p>
                <button @click="$emit('close')" class="bg-gray-200 px-4 sm:px-6 py-2 rounded-lg font-bold text-sm sm:text-base hover:bg-gray-300 transition-colors">
                  {{ $t('pdfPreview.goBackBtn') }}
                </button>
              </div>

              <!-- PDF Ready -->
              <div v-else-if="pdfReady" class="max-w-2xl mx-auto py-6 sm:py-10 text-center px-4">
                <svg class="w-16 h-16 sm:w-20 sm:h-20 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 class="text-lg sm:text-xl font-bold text-gray-800 mb-2">{{ $t('pdfPreview.readyTitle') }}</h4>
                <p class="text-gray-600 mb-6 text-sm sm:text-base">{{ $t('pdfPreview.readyMessage') }}</p>
                
                <div class="bg-blue-50 p-4 sm:p-6 rounded-xl border border-blue-100 text-left mb-6">
                  <h5 class="font-bold mb-2 text-sm sm:text-base">{{ $t('pdfPreview.summaryTitle') }}</h5>
                  <p class="text-xs sm:text-sm"><strong>{{ $t('pdfPreview.summaryReporter') }}</strong> {{ formSummary?.name || 'N/A' }}</p>
                  <p class="text-xs sm:text-sm"><strong>{{ $t('pdfPreview.summaryType') }}</strong> {{ formSummary?.incidentType || 'N/A' }}</p>
                  <p class="text-xs sm:text-sm"><strong>{{ $t('pdfPreview.summaryDate') }}</strong> {{ formSummary?.date || 'N/A' }}</p>
                  <p class="text-xs sm:text-sm"><strong>{{ $t('pdfPreview.summaryLocation') }}</strong> {{ formSummary?.location || 'N/A' }}</p>
                </div>

                <div class="flex flex-col gap-3">
                  <button 
                    @click="openPdfInNewTab" 
                    class="w-full bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {{ $t('pdfPreview.viewBtn') }}
                  </button>
                  <button 
                    @click="downloadPdf" 
                    class="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {{ $t('pdfPreview.downloadBtn') }}
                  </button>
                </div>
              </div>

              <!-- Waiting state -->
              <div v-else class="max-w-2xl mx-auto py-10 text-center">
                <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p class="text-gray-500">{{ $t('pdfPreview.waitingMessage') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 flex-shrink-0">
          <button 
            @click="$emit('close')" 
            class="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 border-2 border-gray-300 rounded-xl font-bold text-gray-600 hover:bg-gray-100 transition-colors text-sm sm:text-base"
          >
            {{ $t('pdfPreview.cancelBtn') }}
          </button>
          <button 
            @click="$emit('confirm')" 
            :disabled="submitting || loading || fetchingPdf || !pdfReady"
            class="w-full sm:w-auto px-8 sm:px-10 py-2.5 sm:py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all text-sm sm:text-base flex items-center justify-center gap-2"
          >
            <svg v-if="submitting" class="animate-spin h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ submitting ? $t('pdfPreview.submittingBtn') : $t('pdfPreview.confirmBtn') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import getAPI from '@/axios-api';
import { sanitizeFileName } from '@/utils/security-utils';

export default {
  name: 'PDFPreview',
  props: {
    show: Boolean,
    loading: Boolean,
    error: String,
    submitting: Boolean,
    incidentId: [String, Number],
    formSummary: {
      type: Object,
      default: () => ({ name: 'N/A', incidentType: 'N/A', date: 'N/A', location: 'N/A' })
    }
  },
  emits: ['close', 'confirm', 'download'],
  data() {
    return {
      pdfBlobUrl: null,
      pdfFilename: null,
      fetchingPdf: false,
      fetchError: null,
      pdfReady: false
    };
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.incidentId) {
          this.fetchPdfPreview();
        } else if (!newVal) {
          this.cleanup();
        }
      }
    }
  },
  methods: {
    getFallbackFilename() {
      const rawName = (this.formSummary && this.formSummary.name) || '';
      const safeName = (sanitizeFileName(rawName).replace(/^_+|_+$/g, '') || (this.$i18n.locale === 'fr' ? 'usager' : 'user'));
      return this.$i18n.locale === 'fr'
        ? `Antic_plainte_de_${safeName}.pdf`
        : `Antic_complaint_of_${safeName}.pdf`;
    },

    async fetchPdfPreview() {
      if (!this.incidentId) {
        this.fetchError = 'No incident ID provided';
        return;
      }

      this.cleanup();
      this.fetchingPdf = true;
      this.fetchError = null;
      this.pdfReady = false;

      try {
        const token = localStorage.getItem(`pdf_token_${this.incidentId}`);
        
        const params = new URLSearchParams();
        params.append('incident_id', this.incidentId);
        if (token) {
          params.append('token', token);
        }

        const response = await getAPI.get(`/api/complaint-pdf/preview/?${params.toString()}`, {
          responseType: 'blob',
          timeout: 60000
        });

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            this.pdfFilename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        // Create blob URL if we got data
        if (response.data && response.data.size > 0) {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          this.pdfBlobUrl = URL.createObjectURL(blob);
          this.pdfReady = true;
        } else {
          throw new Error('Empty PDF response');
        }
      } catch (err) {
        this.fetchError = err.response?.data?.error || err.message || 'Failed to load PDF';
      } finally {
        this.fetchingPdf = false;
      }
    },

    openPdfInNewTab() {
      if (this.pdfBlobUrl) {
        window.open(this.pdfBlobUrl, '_blank');
      }
    },

    downloadPdf() {
      if (this.pdfBlobUrl) {
        const link = document.createElement('a');
        link.href = this.pdfBlobUrl;
        link.download = this.getFallbackFilename();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        this.$emit('download');
      }
    },

    cleanup() {
      if (this.pdfBlobUrl) {
        URL.revokeObjectURL(this.pdfBlobUrl);
        this.pdfBlobUrl = null;
      }
      this.pdfFilename = null;
      this.pdfReady = false;
      this.fetchError = null;
    }
  },
  beforeUnmount() {
    this.cleanup();
  }
}
</script>

<style scoped>
.modal-fade-enter-active, 
.modal-fade-leave-active { 
  transition: opacity 0.3s ease; 
}
.modal-fade-enter-from, 
.modal-fade-leave-to { 
  opacity: 0; 
}
</style>